import React from 'react';
import { Box, ButtonBase, Drawer, Toolbar, Typography, useTheme, IconButton } from '@mui/material';
import { Close as CloseIcon, ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NavSection from './NavSection';
import { useSectionExpandedState } from './useSectionExpandedState';
import { fs } from "../../theme/designLintTokens";
const drawerWidth = 280;
const collapsedDrawerWidth = 60;

/** Opaque chrome surface — ambient glass retired in A3 (DR-1 / D4). */
const chromeDrawerPaper = (theme, collapsed = false) => {
  const chrome = theme.palette.ef?.chrome ?? theme.palette.background.paper;
  const edge = theme.palette.ef?.edge ?? theme.palette.divider;
  const width = collapsed ? collapsedDrawerWidth : drawerWidth;
  return {
    boxSizing: 'border-box',
    width,
    backgroundImage: 'none',
    boxShadow: 'none',
    backgroundColor: chrome,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderRight: `1px solid ${edge}`,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.short,
    }),
  };
};

/**
 * @param {object} props
 * @param {boolean} props.mobileOpen
 * @param {() => void} props.onDrawerToggle
 * @param {string} props.brandPath
 * @param {Array<{ id: string, title: string, initiallyExpanded?: boolean, items: Array<{ id: string, label: string, path?: string, matchPath?: string, icon?: React.ReactNode, action?: string }> }>} props.sections
 */
const Sidebar = ({
  mobileOpen,
  onDrawerToggle,
  brandPath,
  sections,
  collapsed = false,
  onCollapseToggle
}) => {
  const theme = useTheme();
  const {
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isExpanded,
    toggle
  } = useSectionExpandedState(sections, location.pathname);
  const handleNavigate = path => {
    navigate(path);
    if (mobileOpen) onDrawerToggle();
  };
  const handleBrandClick = () => {
    const raw = brandPath || '/faculty/dashboard';
    const target = String(raw).startsWith('/') ? raw : `/${raw}`;
    navigate(target);
    if (mobileOpen) onDrawerToggle();
  };
  const handleItemActivate = item => {
    if (item.action === 'logout') {
      logout();
      if (mobileOpen) onDrawerToggle();
      return;
    }
    if (item.path) handleNavigate(item.path);
  };
  const brandBorder = `1px solid ${theme.palette.divider}`;
  // Properly join PUBLIC_URL with path to avoid double slashes
  const baseUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const appIconUrl = `${baseUrl}/app-icon.png`;
  const currentDrawerWidth = onCollapseToggle && collapsed ? collapsedDrawerWidth : drawerWidth;

  const desktopToolbarContent = (
    <Toolbar sx={{
      minHeight: { xs: 64, sm: 72 },
      px: collapsed && onCollapseToggle ? 1 : 2.5,
      py: 1.5,
      borderBottom: brandBorder,
      display: 'flex',
      justifyContent: collapsed && onCollapseToggle ? 'center' : 'space-between',
      alignItems: 'center',
      gap: 1
    }}>
      <ButtonBase type="button" onClick={handleBrandClick} disableRipple aria-label="Go to home" sx={{
        textAlign: 'left',
        borderRadius: '10px',
        px: 1,
        py: 0.75,
        mx: -0.5,
        transition: 'background-color 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flex: collapsed && onCollapseToggle ? 0 : 1,
        minWidth: 0,
        '&:hover': {
          bgcolor: theme.palette.action.hover
        }
      }}>
        <Box component="img" src={appIconUrl} alt="Nirman" sx={{
          height: 32,
          width: 'auto',
          maxWidth: 48,
          objectFit: 'contain',
          flexShrink: 0,
          display: { xs: 'none', sm: 'block' }
        }} />
        {!(collapsed && onCollapseToggle) && (
          <Typography variant="h2" component="span" sx={{
            letterSpacing: '-0.03em',
            color: 'text.primary',
            fontSize: fs.s_n1_625rem,
            display: { xs: 'none', sm: 'inline' }
          }}>
            Nirman
          </Typography>
        )}
      </ButtonBase>
      {/* Collapse/Expand toggle button */}
      {onCollapseToggle && (
        <IconButton
          onClick={onCollapseToggle}
          size="small"
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flexShrink: 0,
            color: 'text.secondary'
          }}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        >
          {collapsed ? <ChevronLeftIcon sx={{ transform: 'scaleX(-1)' }} /> : <ChevronLeftIcon />}
        </IconButton>
      )}
    </Toolbar>
  );

  const mobileToolbarContent = (
    <Toolbar sx={{
      minHeight: 64,
      px: 2.5,
      py: 1.5,
      borderBottom: brandBorder,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        <Box component="img" src={appIconUrl} alt="Nirman" sx={{
          height: 32,
          width: 'auto',
          maxWidth: 48,
          objectFit: 'contain',
          flexShrink: 0
        }} />
        <Typography variant="h2" component="span" sx={{
          letterSpacing: '-0.03em',
          color: 'text.primary',
          fontSize: fs.s_n1_625rem,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          Nirman
        </Typography>
      </Box>
      <IconButton
        onClick={onDrawerToggle}
        size="small"
        sx={{ flexShrink: 0, color: 'text.secondary' }}
        aria-label="Close navigation"
      >
        <CloseIcon />
      </IconButton>
    </Toolbar>
  );

  const navigationSections = (
    <Box component="nav" sx={{
      flex: 1,
      overflowY: 'auto',
      py: 3,
      px: 1.75
    }}>
      {sections.map(section => <Box key={section.id} sx={{
        mb: section.collapsible === false ? 2 : 3.25,
        ...(section.id === 'profile' && {
          pt: 2,
          mt: 0.5,
          borderTop: '1px solid',
          borderColor: theme.palette.divider
        })
      }}>
        <NavSection sectionId={section.id} title={section.title ?? ''} items={section.items} pathname={location.pathname} onItemActivate={handleItemActivate} expanded={isExpanded(section.id)} onToggle={() => toggle(section.id)} collapsible={section.collapsible !== false} collapsed={onCollapseToggle ? collapsed : false} />
      </Box>)}
    </Box>
  );
  return <Box component="nav" sx={{
    width: {
      xs: 0,
      sm: currentDrawerWidth
    },
    flexShrink: {
      xs: 0,
      sm: 0
    },
    transition: theme => theme.transitions.create('width', {
      duration: theme.transitions.duration.short,
    }),
  }} aria-label="sidebar navigation">
      <Drawer variant="temporary" open={mobileOpen} onClose={onDrawerToggle} ModalProps={{
        keepMounted: true
      }} sx={t => ({
        display: {
          xs: 'block',
          sm: 'none'
        },
        '& .MuiDrawer-paper': chromeDrawerPaper(t, false)
      })}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          {mobileToolbarContent}
          {navigationSections}
        </Box>
      </Drawer>
      <Drawer variant="permanent" sx={t => ({
        display: {
          xs: 'none',
          sm: 'block'
        },
        '& .MuiDrawer-paper': chromeDrawerPaper(t, collapsed && onCollapseToggle ? collapsed : false)
      })} open>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
          {desktopToolbarContent}
          {navigationSections}
        </Box>
      </Drawer>
    </Box>;
};
export { drawerWidth };
export default Sidebar;
